import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../Api"
import { TrustMemberDto, TrustsFormData } from "../../types/Trust"
import UnsavedChangesGuard from "../../UnsavedChanges"
import { TrashIcon } from "@heroicons/react/24/outline"
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup";
import * as css from "../../constants/css";
import FormInput from "../../controls/FormInput";
import { FormSelect } from "../../controls/FormSelect";
import * as regex from "../../constants/regex";

const convertDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns yyyy-MM-dd format
};

const formSchema = yup
    .object().shape({
        trusts: yup.array().of(
            yup.object().shape({
                name: yup.string().required("Trust Name is required"),
                trustType: yup.string().required("Trust Type is required"),
                trustJurisdiction: yup.string().required("Trust Jurisdiction is required"),
                tin: yup.string().required("TIN is required"),
                dateOfFormation: yup.string().required("Date of Formation is required").test('not-future', 'Date of formation cannot be in the future', function (value) {
                    if (!value) return true; // Let required validation handle empty values
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999); // Set to end of today
                    return selectedDate <= today;
                }),
                trustMembers: yup.array().of(
                    yup.object().shape({
                        type: yup.string().required("Type is required"),
                        companyWebsite: yup.string().required("Company Website is required").matches(regex.url, "Please enter a valid domain (e.g., google.com)"),
                        email: yup.string().matches(regex.email, "Must be a valid email").required("Email is required"),
                        phoneNumber: yup.string().required("Phone Number is required").matches(regex.phoneNumber, "Phone Number must be 4-12 digits"),
                        contactNotes: yup.string()
                    })
                ).min(1, "At least one trust member is required")
            })
        ).min(0, "Trusts are optional")
    });

const TrustPage: React.FC = (route) => {
    const api = useApi();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [notFoundError, setNotFoundError] = useState<string | null>(null);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    const { register, control, formState: { errors, isDirty }, handleSubmit, reset, setValue, watch, trigger } = useForm<TrustsFormData>({
        //@ts-ignore
        resolver: yupResolver<TrustsFormData>(formSchema),
        mode: "onSubmit"
    });

    const { append: appendTrust } = useFieldArray({
        control,
        name: "trusts"
    });

    // Watch form values to use in the form
    const watchedValues = watch();
    const watchedTrusts = watch("trusts") || [];
    const visibleTrusts = watchedTrusts.filter(trust => !trust.deleted);

    useEffect(() => {
        const populateExistingData = async () => {
            try {
                setNotFoundError(null);
                const response = await api.get("/api/onboarding/GetTrustsData");
                const trusts = response.data.trusts || [];

                // Convert dates for input fields
                const trustsWithConvertedDates = trusts.map((trust: any) => ({
                    ...trust,
                    dateOfFormation: convertDateForInput(trust.dateOfFormation)
                }));

                // Reset form with the loaded data
                reset({
                    trusts: trustsWithConvertedDates
                });

            } catch (error: any) {
                // Redirect all errors to generic error page
                navigate("/error", {
                    state: {
                        error: error.response?.data || "An error occurred while fetching data",
                        status: error.response?.status || "network"
                    }
                });
                return;
            } finally {
                setLoading(false);
            }
        };

        populateExistingData();
    }, [api, navigate, reset]);

    const onSubmit = async (data: TrustsFormData) => {
        setHasAttemptedSubmit(true);
        setSubmitting(true);
        try {
            // Filter out soft-deleted trusts before saving
            const activeTrusts = data.trusts.filter(trust => !trust.deleted);

            await api.post("/api/onboarding/SaveTrusts", { trusts: activeTrusts });
            navigate("/onboarding/brokerage-custodian");
        } catch (error: any) {
            navigate("/error", {
                state: {
                    error: "An error occurred while saving trust information",
                    status: 500
                }
            });
        } finally {
            setSubmitting(false);
        }
    };

    const removeTrust = async (displayIndex: number) => {
        const currentTrusts = watch("trusts") || [];
        const updatedTrusts = [...currentTrusts];

        // Find the actual index in the original array by skipping deleted items
        let actualIndex = -1;
        let visibleCount = 0;

        for (let i = 0; i < updatedTrusts.length; i++) {
            const trust = updatedTrusts[i];
            if (trust && !trust.deleted) {
                if (visibleCount === displayIndex) {
                    actualIndex = i;
                    break;
                }
                visibleCount++;
            }
        }

        // Check if we found the trust at the given display index
        if (actualIndex >= 0) {
            const trust = updatedTrusts[actualIndex];
            if (trust) {
                // Mark the trust as deleted instead of removing from array
                if (trust.id && trust.id > 0) {
                    // This is an existing trust - call the DELETE API
                    try {
                        await api.delete(`/api/onboarding/DeleteTrust/${trust.id}`);
                        // Mark as deleted in the UI
                        updatedTrusts[actualIndex] = {
                            ...trust,
                            deleted: true
                        };
                    } catch (error: any) {
                        console.error("Error deleting trust:", error);
                        // Handle error - could show a toast notification
                    }
                } else {
                    // This is a new trust - remove from array
                    updatedTrusts.splice(actualIndex, 1);
                }
            }
        }

        setValue("trusts", updatedTrusts);
    };

    const addTrust = () => {
        appendTrust({
            id: 0,
            name: "",
            trustType: "",
            trustJurisdiction: "",
            tin: "",
            dateOfFormation: "",
            individualId: 0,
            trustMembers: []
        });
    };

    const addTrustMember = (trustIndex: number, type: string) => {
        const currentTrusts = watchedValues.trusts || [];
        const currentTrust = currentTrusts[trustIndex];
        if (currentTrust) {
            const newMember: TrustMemberDto = {
                id: 0,
                type: type,
                companyWebsite: "",
                email: "",
                phoneNumber: "",
                contactNotes: "",
                trustId: currentTrust.id || 0
            };

            const updatedTrusts = [...currentTrusts];
            updatedTrusts[trustIndex] = {
                ...currentTrust,
                trustMembers: [...(currentTrust.trustMembers || []), newMember]
            };

            setValue(`trusts.${trustIndex}`, updatedTrusts[trustIndex]);
            // Trigger re-validation to clear the "At least one trust member is required" error
            trigger(`trusts.${trustIndex}.trustMembers`);
        }
    };

    const removeTrustMember = async (trustIndex: number, memberIndex: number) => {
        const currentTrusts = watchedValues.trusts || [];
        const currentTrust = currentTrusts[trustIndex];
        if (currentTrust && currentTrust.trustMembers) {
            const trustMember = currentTrust.trustMembers[memberIndex];

            if (trustMember?.id && trustMember.id > 0) {
                // Existing trust member - call DELETE API
                try {
                    await api.delete(`/api/onboarding/DeleteTrustMember/${trustMember.id}`);
                    // Remove from array after successful API call
                    const updatedMembers = currentTrust.trustMembers.filter((_, index) => index !== memberIndex);
                    const updatedTrusts = [...currentTrusts];
                    updatedTrusts[trustIndex] = {
                        ...currentTrust,
                        trustMembers: updatedMembers
                    };
                    setValue(`trusts.${trustIndex}`, updatedTrusts[trustIndex]);
                    // Trigger re-validation to update the "At least one trust member is required" error
                    trigger(`trusts.${trustIndex}.trustMembers`);
                } catch (error: any) {
                    console.error("Error deleting trust member:", error);
                    // Handle error - could show a toast notification
                }
            } else {
                // New trust member - remove from array
                const updatedMembers = currentTrust.trustMembers.filter((_, index) => index !== memberIndex);
                const updatedTrusts = [...currentTrusts];
                updatedTrusts[trustIndex] = {
                    ...currentTrust,
                    trustMembers: updatedMembers
                };
                setValue(`trusts.${trustIndex}`, updatedTrusts[trustIndex]);
                // Trigger re-validation to update the "At least one trust member is required" error
                trigger(`trusts.${trustIndex}.trustMembers`);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 text-center">Loading trust data...</p>
                </div>
            </div>
        );
    }

    if (notFoundError) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold mb-2">Data Not Found</h1>
                        <p className="mb-6">{notFoundError}</p>

                        <div className="space-y-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full btn-primary"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="w-full btn-secondary"
                            >
                                Go to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto">
            <UnsavedChangesGuard hasUnsavedChanges={isDirty}></UnsavedChangesGuard>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-6">
                    {/* Header Card */}
                    <div className="bg-white rounded-xl shadow-md">
                        <div className="px-6 py-8">
                            <h1 className="text-3xl font-bold mb-2">Trust Information</h1>
                            <p className="text-lg">Add and manage your trusts. You can skip this step if you don't have any trusts.</p>
                        </div>
                    </div>

                    {/* Trusts List */}
                    {visibleTrusts.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <div className="text-center py-8">
                                <h2 className="text-xl font-semibold text-gray-700 mb-4">No Trusts</h2>
                                <p className="text-gray-600 mb-6">
                                    If you have trusts to add, click the button below to get started.
                                </p>
                                <button
                                    type="button"
                                    onClick={addTrust}
                                    className="btn-primary-lg mx-auto"
                                >
                                    + Add Your First Trust
                                </button>
                            </div>
                        </div>
                    ) : (
                        visibleTrusts.map((trust, trustIndex) => {
                            // Find the actual index in the original array by counting visible trusts
                            let actualIndex = -1;
                            let visibleCount = 0;

                            for (let i = 0; i < watchedTrusts.length; i++) {
                                const t = watchedTrusts[i];
                                if (t && !t.deleted) {
                                    if (visibleCount === trustIndex) {
                                        actualIndex = i;
                                        break;
                                    }
                                    visibleCount++;
                                }
                            }

                            return (
                                <div key={trust.id || `trust-${trustIndex}`} className="bg-white rounded-xl shadow-md p-6">
                                    <div className="flex justify-between items-center mb-6">
                                        <h2 className="text-xl font-semibold">Trust #{trustIndex + 1}</h2>
                                        <button
                                            type="button"
                                            onClick={() => removeTrust(trustIndex)}
                                            className="btn-danger"
                                        >
                                            <TrashIcon />
                                            Remove Trust
                                        </button>
                                    </div>

                                    {/* Trust Details */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <FormInput label="Trust Name" name={`trusts.${actualIndex}.name`} register={register} errors={errors} />

                                        <Controller
                                            control={control}
                                            name={`trusts.${actualIndex}.trustType`}
                                            render={({ field }) => (
                                                <FormSelect
                                                    label="Trust Type"
                                                    isSearchable={false}
                                                    isClearable={false}
                                                    placeholder="Select Trust Type"
                                                    value={field.value || null}
                                                    options={[
                                                        { value: "IrrevocableTrust", label: "Irrevocable Trust" },
                                                        { value: "RevocableTrust", label: "Revocable Trust" },
                                                    ]}
                                                    onChange={(val) => field.onChange((val as string) ?? '')}
                                                    error={errors.trusts?.[actualIndex]?.trustType?.message}
                                                />
                                            )}
                                        />

                                        <FormInput label="Trust Jurisdiction" name={`trusts.${actualIndex}.trustJurisdiction`} register={register} errors={errors} />

                                        <FormInput label="TIN" name={`trusts.${actualIndex}.tin`} register={register} errors={errors} />

                                        <FormInput label="Date of Formation" name={`trusts.${actualIndex}.dateOfFormation`} type="date" register={register} errors={errors} />
                                    </div>

                                    {/* Trust Members Section */}
                                    <div className="border-t pt-6">
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold">Trust Members</h3>
                                            <p className="text-gray-600">Manage trustees, trust protectors, and distribution trustees</p>
                                        </div>

                                        {/* Add Trust Member Buttons */}
                                        <div className="mb-6 space-x-4">
                                            <button
                                                type="button"
                                                onClick={() => addTrustMember(actualIndex, "Trustee")}
                                                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors text-white"
                                            >
                                                Add Trustee
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => addTrustMember(actualIndex, "DistributionTrustee")}
                                                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors text-white"
                                            >
                                                Add Distribution Trustee
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => addTrustMember(actualIndex, "TrustProtector")}
                                                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors text-white"
                                            >
                                                Add Trust Protector
                                            </button>
                                        </div>

                                        {/* Trust Members List */}
                                        {watchedValues.trusts?.[actualIndex]?.trustMembers?.map((member, memberIndex) => (
                                            <div key={memberIndex} className="border rounded-lg p-4 mb-4">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-md font-medium">{member.type.replace(/([A-Z])/g, ' $1').trim()}</h4>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTrustMember(trustIndex, memberIndex)}
                                                        className="btn-danger"
                                                    >
                                                        <TrashIcon />
                                                        Remove Member
                                                    </button>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <FormInput label="Company Website" name={`trusts.${actualIndex}.trustMembers.${memberIndex}.companyWebsite`} register={register} errors={errors} />

                                                    <FormInput label="Email" name={`trusts.${actualIndex}.trustMembers.${memberIndex}.email`} type="email" register={register} errors={errors} />

                                                    <FormInput label="Phone Number" name={`trusts.${actualIndex}.trustMembers.${memberIndex}.phoneNumber`} type="tel" register={register} errors={errors} />

                                                    <div>
                                                        <label className={css.label}>Contact Notes</label>
                                                        <textarea {...register(`trusts.${actualIndex}.trustMembers.${memberIndex}.contactNotes`)} className={css.textBox} rows={3} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {hasAttemptedSubmit && errors.trusts?.[actualIndex]?.trustMembers && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                                <p role="alert" className="text-red-600 font-medium">{errors.trusts[actualIndex]?.trustMembers?.message}</p>
                                            </div>
                                        )}
                                        {hasAttemptedSubmit && (!watchedValues.trusts?.[actualIndex]?.trustMembers || watchedValues.trusts[actualIndex].trustMembers.length === 0) && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                                <p className="text-yellow-700 font-medium">⚠️ At least one trust member is required for this trust.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    {visibleTrusts.length > 0 && (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={addTrust}
                                className="btn-primary-lg mx-auto"
                            >
                                + Add Another Trust
                            </button>
                        </div>
                    )}

                    {/* Submit Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="mb-6">
                            <h2 className="text-xl font-semibold">Continue to Brokerage/Custodian Information</h2>
                            <p className="text-gray-600 mt-2">Please review the information above. If everything looks correct, click Continue to proceed.</p>
                        </div>

                        <div className="flex justify-between">
                            <button
                                type="button"
                                onClick={() => navigate("/onboarding/Business")}
                                disabled={submitting}
                                className="btn-secondary"
                            >
                                Go Back
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary"
                            >
                                {submitting ? (
                                    <>
                                        <div className={css.spinner}></div>
                                        Continuing...
                                    </>
                                ) : (
                                    'Continue'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default TrustPage;
